import { MutableRefObject, useLayoutEffect, useRef } from 'react';
import { TableauChartDto } from '@asset-metrix/portal-gateway-client-js';
import { LoadingIndicator, Widget } from '@asset-metrix/portal-ui';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';

import { getTableauToken } from '../../api/portalApi/tableau';
import { fetchPage, PageQueryKey } from '../../api/portalGateway/page';
import { usePageFilterQuery } from '../../hooks/filter';
import { useSelections } from '../../store/hooks/useSelections';
import { prependRoleId } from '../../utils/prependRoleId';
import { PageFilters } from '../filter/PageFilters';
import { WidgetFilterContainer } from '../filter/WidgetFilterContainer';
import { useContentEditActions } from '../page/useContentEditActions';

import {
  getTableauIdentifier,
  getTableauIframeUrl,
} from './tableauChartFilterHelper';
import {
  BaseUseTableauChartActionProps,
  TableauViz,
  useTableauChartActions,
} from './useTableauChartActions';

import styles from './tableauChart.module.scss';

const Tableau = ({
  config,
  isFullscreen,
  pageFilterInstanceId,
  pageId,
  tableauInstance,
}: Pick<TableauChartDto, 'config'> & { isFullscreen?: boolean } & {
  tableauInstance: MutableRefObject<Promise<TableauViz> | undefined>;
  pageFilterInstanceId: string;
  pageId: string;
}) => {
  const tableauRef = useRef<HTMLDivElement>(null);

  const pageFilterQuery = usePageFilterQuery(
    pageId,
    pageFilterInstanceId,
    undefined
  );
  const pageFilterSelections = useSelections(pageFilterInstanceId);

  useLayoutEffect(() => {
    if (
      pageFilterQuery.isSuccess &&
      tableauRef.current &&
      tableauInstance.current == null
    ) {
      try {
        tableauInstance.current = getTableauToken().then((result) => {
          return new (globalThis as any).tableau.Viz(
            tableauRef.current,
            getTableauIframeUrl(
              result.tableau_token,
              config,
              pageFilterSelections
            ),
            {
              hideTabs: true,
              hideToolbar: true,
              onFirstInteractive: function () {
                console.info('Tableau chart initialized');
              },
            }
          );
        });
      } catch (error: unknown) {
        console.warn(error);
      }
    }

    return () => {
      tableauInstance.current?.then((viz) => viz.dispose()).catch((err) => err);
      tableauInstance.current = undefined;
    };
  }, [
    pageFilterSelections,
    pageFilterQuery.isSuccess,
    tableauInstance,
    config,
  ]);

  const tableauIdentifier = getTableauIdentifier(config);

  return (
    <div
      id={tableauIdentifier}
      style={
        isFullscreen ? undefined : { height: config.heightfactor * 70 + 'vh' }
      }
      data-testid={tableauIdentifier}
      ref={tableauRef}
      className={styles['container']}
    >
      {pageFilterQuery.isFetching && (
        <div
          className={clsx(styles['overlay'], styles['overlay--initialized'])}
        >
          <LoadingIndicator />
        </div>
      )}
    </div>
  );
};

export type TableauChartContentProps = {
  collapsible: boolean;
  enableScrollElementToTop?: boolean;
  entity: TableauChartDto;
  isFullscreen?: boolean;
  customPageFilterStoreId?: string | null;
  pageId: string;
  showPageFilter?: boolean;
};

export function TableauChartContent({
  collapsible,
  enableScrollElementToTop,
  entity,
  isFullscreen,
  pageFilterInstanceId,
  pageId,
  customPageFilterStoreId,
  showPageFilter = false,
  title,
  widgetId,
  ...useTableauChartActionProps
}: TableauChartContentProps & BaseUseTableauChartActionProps) {
  const { data: page } = useQuery({
    queryKey: prependRoleId<PageQueryKey>(['page', pageId ?? 'unknown']),
    queryFn: fetchPage,

    enabled: showPageFilter,
  });

  const tableauInstance: MutableRefObject<Promise<TableauViz> | undefined> =
    useRef(undefined);

  const widgetActions = useTableauChartActions({
    config: entity.config,
    tableauInstance,
    title,
    widgetId,
    pageFilterInstanceId,
    ...useTableauChartActionProps,
  });
  const actions = useContentEditActions(pageId, widgetId, widgetActions);

  return (
    <Widget
      collapsible={collapsible}
      enableScrollElementToTop={enableScrollElementToTop}
      title={title}
      actions={actions}
    >
      {showPageFilter && page != null ? (
        <WidgetFilterContainer>
          <PageFilters
            config={page.data.pageFilter}
            filterKey={pageFilterInstanceId}
            customPageFilterStoreId={customPageFilterStoreId}
            pageId={pageId}
            embedded={true}
          />
        </WidgetFilterContainer>
      ) : null}
      <Tableau
        config={entity.config}
        isFullscreen={isFullscreen}
        pageFilterInstanceId={pageFilterInstanceId}
        pageId={pageId}
        tableauInstance={tableauInstance}
      />
    </Widget>
  );
}
